import { CommonModule } from '@angular/common';
import type { OnDestroy, OnInit } from '@angular/core';
import { Component, TemplateRef, ViewChild } from '@angular/core';
import { WebinarScheduleService } from '@core-mkt/services/webinar-schedule/webinar-schedule.service';
import { WysiwygRedactorService } from '@core-mkt/services/wysiwg-redactor/wysiwyg-redactor.service';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faAngleDown, faX } from '@fortawesome/free-solid-svg-icons';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {
  CategoryCode,
  isCategoryCode,
  isWebinarCategory,
  WebinarCategory,
  type WebinarScheduleEventList,
  type WebinarScheduleResponse,
  type WebinarScheduleSessionList,
} from './webinar-schedule';

@Component({
  selector: 'webinar-schedule',
  templateUrl: './webinar-schedule.component.html',
  standalone: true,
  imports: [CommonModule, FontAwesomeModule],
})
export class WebinarScheduleComponent implements OnInit, OnDestroy {
  webinarSchedule: WebinarScheduleResponse[];
  faAngleDown = faAngleDown;
  faX = faX;
  openSessions: boolean[] = []; // Array to track open state by index
  content: WebinarScheduleResponse[];
  shouldShow = false;
  webinarTitle: string;
  webinarDescription: string;
  webinarUpcomingLabel: string;
  webinarLayout: TemplateRef<unknown> | null = null;
  private destroy$ = new Subject<void>();
  @ViewChild('BrokerPreLicenseTpl', { static: true }) BrokerPreLicenseTpl!: TemplateRef<unknown>;
  @ViewChild('ManagingBrokerPreLicenseTpl', { static: true }) ManagingBrokerPreLicenseTpl!: TemplateRef<unknown>;
  @ViewChild('ResidentialLeasingAgentPreLicenseTpl', { static: true })
  ResidentialLeasingAgentPreLicenseTpl!: TemplateRef<unknown>;
  @ViewChild('MTGPreLicenseTpl', { static: true }) MTGPreLicenseTpl!: TemplateRef<unknown>;
  @ViewChild('INSPreLicenseTpl', { static: true }) INSPreLicenseTpl!: TemplateRef<unknown>;
  @ViewChild('REContinuingEdTpl', { static: true }) REContinuingEdTpl!: TemplateRef<unknown>;
  @ViewChild('INSContinuingEdTpl', { static: true }) INSContinuingEdTpl!: TemplateRef<unknown>;
  @ViewChild('MTGContinuingEdTpl', { static: true }) MTGContinuingEdTpl!: TemplateRef<unknown>;

  constructor(
    private readonly webinarScheduleService: WebinarScheduleService,
    private redactor: WysiwygRedactorService,
  ) {
    this.webinarScheduleService.showWebinarSchedule.pipe(takeUntil(this.destroy$)).subscribe((show) => {
      this.shouldShow = show;

      if (show && typeof document !== 'undefined') {
        document.body.style.overflow = 'hidden';
      } else if (typeof document !== 'undefined') {
        document.body.style.overflow = '';
      }
    });

    this.webinarScheduleService.webinarContent
      .pipe(takeUntil(this.destroy$))
      .subscribe((content: WebinarScheduleResponse[]) => {
        this.webinarSchedule = content;
        this.webinarTitle = this.setWebinarTitle();
        this.webinarDescription = this.setWebinarDescription();

        if (content && content.length) {
          this.openSessions = new Array(content.reduce((count, course) => count + course.sessionList.length, 0)).fill(
            false,
          ) as boolean[];

          this.webinarUpcomingLabel = this.setUpcomingLabel();
          this.webinarLayout = this.setLayout();

          this.webinarSchedule?.forEach((schedule) => {
            if (schedule.webinarContent) {
              schedule.webinarContent = this.redactor.bypassSanitizer(schedule.webinarContent);
            }
          });
        }
      });
  }

  get title(): string {
    return this.webinarTitle;
  }

  get description(): string {
    return this.webinarDescription;
  }

  get upcomingLabel(): string {
    return this.webinarUpcomingLabel;
  }

  get layout(): TemplateRef<unknown> | null {
    return this.webinarLayout;
  }

  get hasScheduleData(): boolean {
    return this.webinarSchedule?.length > 0;
  }

  ngOnInit(): void {
    if (typeof window !== 'undefined') {
      window.addEventListener('keydown', this.handleEscKey);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();

    if (typeof window !== 'undefined') {
      window.removeEventListener('keydown', this.handleEscKey);
    }
  }

  private handleEscKey = (event: KeyboardEvent): void => {
    if (event.key === 'Escape' && this.shouldShow) {
      this.closeSchedule(event, this.webinarSchedule);
    }
  };

  setWebinarTitle(): string {
    const rawCategory = this.webinarSchedule?.[0]?.category?.title;
    const rawCode = this.webinarSchedule?.[0]?.category?.code;
    const category = isWebinarCategory(rawCategory) ? rawCategory : undefined;
    const code = isCategoryCode(rawCode) ? rawCode : undefined;
    const webinarTitle = this.webinarSchedule?.[0]?.webinarTitle;

    if (webinarTitle) {
      return webinarTitle;
    } else {
      if (!category) {
        return 'Webinar Schedule';
      }

      switch (category) {
        case WebinarCategory.BrokerPreLicense:
          return 'Basic Broker Pre-Licensing';
        case WebinarCategory.ManagingBrokerPreLicense:
          return 'Managing Broker Pre-Licensing';
        case WebinarCategory.ResidentialLeasingAgentPreLicense:
          return 'Residential Leasing Agent Pre-Licensing';
        case WebinarCategory.PreLicense:
          switch (code) {
            case CategoryCode.MTG:
              return 'Mortgage Pre-Licensing';
            case CategoryCode.INS:
              return 'Insurance Pre-Licensing';
          }
          break;
        case WebinarCategory.ContinuingEd:
          switch (code) {
            case CategoryCode.RE:
              return 'Real Estate Continuing Ed';
            case CategoryCode.INS:
              return 'Insurance Ethics Continuing Ed';
            case CategoryCode.MTG:
              return 'Mortgage Continuing Education';
          }
          break;
      }

      return 'Webinar Schedule';
    }
  }

  setWebinarDescription(): string {
    const { title: rawCategory, code: rawCode } = this.webinarSchedule?.[0]?.category || {};
    const category = isWebinarCategory(rawCategory) ? rawCategory : undefined;
    const code = isCategoryCode(rawCode) ? rawCode : undefined;
    const webinarContent = this.webinarSchedule?.[0]?.webinarContent;
    if (webinarContent) {
      // TODO: Validate that it is working correctly on the other schedules
      return webinarContent;
    } else {
      if (!category) {
        return '';
      }

      switch (category) {
        case WebinarCategory.BrokerPreLicense:
        case WebinarCategory.ManagingBrokerPreLicense:
          return 'Students must attend six live webinar interactive sessions (1-6). You may complete the sessions in any order, and you do not need to begin with Session 1.';
        case WebinarCategory.ResidentialLeasingAgentPreLicense:
          return this.redactor.bypassSanitizer(
            'This two-day webinar series is <strong>consecutive</strong>, meaning students must attend both days.',
          );
        case WebinarCategory.PreLicense:
          switch (code) {
            case CategoryCode.MTG:
              return 'This three-day webinar series is consecutive, meaning students must attend all three days.';
            case CategoryCode.INS:
              return 'This one-day webinar is scheduled based on the following cadence (excluding holidays):';
          }
          break;
        case WebinarCategory.ContinuingEd:
          switch (code) {
            case CategoryCode.RE:
              return "Earn your CE by attending the upcoming webinar session(s) below. You will be able to schedule your session(s) after enrolling. Enrolling in a bundle? You don't need to complete sessions in consecutive order.";
            case CategoryCode.MTG:
              return this.redactor.bypassSanitizer(
                `<p>This live, interactive one-day webinar satisfies your 7-Hour Core or 8-Hour Comprehensive Mortgage Loan Originator CE requirement.</p>
<p>Learn from licensed mortgage professionals who bring current industry experience, practical examples, and real-world expertise into every course.</p>
<p>You'll cover: federal law updates, AI in mortgage lending, VA loans &amp; appraisal standards, and identity theft prevention (8-Hour only) through live discussion and case studies, not just slides.</p>`.trim(),
              );
            case CategoryCode.INS:
              return 'This 3-hour webinar is offered multiple times per week (usually 9-10 times per month).';
          }
          break;
      }

      return '';
    }
  }

  setUpcomingLabel(): string {
    const { title: rawCategory, code: rawCode } = this.webinarSchedule?.[0]?.category || {};
    const category = isWebinarCategory(rawCategory) ? rawCategory : undefined;
    const code = isCategoryCode(rawCode) ? rawCode : undefined;

    if (!category) {
      return 'Upcoming Events:';
    }

    switch (category) {
      case WebinarCategory.BrokerPreLicense:
      case WebinarCategory.ManagingBrokerPreLicense:
      case WebinarCategory.ResidentialLeasingAgentPreLicense:
      case WebinarCategory.ContinuingEd:
        return 'Upcoming Dates:';
      case WebinarCategory.PreLicense:
        switch (code) {
          case CategoryCode.MTG:
            return 'Upcoming Webinars:';
          case CategoryCode.INS:
            return 'Upcoming Dates:';
        }
        break;
    }

    return 'Upcoming Events:';
  }

  setLayout(): TemplateRef<unknown> | null {
    if (!this.webinarSchedule?.length) {
      return null;
    }

    const { title: rawCategory, code: rawCode } = this.webinarSchedule?.[0].category || {};
    const category = isWebinarCategory(rawCategory) ? rawCategory : undefined;
    const code = isCategoryCode(rawCode) ? rawCode : undefined;

    switch (category) {
      case WebinarCategory.BrokerPreLicense:
        return this.BrokerPreLicenseTpl;
      case WebinarCategory.ManagingBrokerPreLicense:
        return this.ManagingBrokerPreLicenseTpl;
      case WebinarCategory.ResidentialLeasingAgentPreLicense:
        return this.ResidentialLeasingAgentPreLicenseTpl;
      case WebinarCategory.ContinuingEd:
        return code === CategoryCode.RE
          ? this.REContinuingEdTpl
          : code === CategoryCode.INS
          ? this.INSContinuingEdTpl
          : code === CategoryCode.MTG
          ? this.MTGContinuingEdTpl
          : null;
      case WebinarCategory.PreLicense:
        return code === CategoryCode.MTG
          ? this.MTGPreLicenseTpl
          : code === CategoryCode.INS
          ? this.INSPreLicenseTpl
          : null;
      default:
        return null;
    }
  }

  closeSchedule(event: Event, content: WebinarScheduleResponse[]): void {
    event.preventDefault();
    this.webinarScheduleService.setShowWebinarSchedule(false, content);
  }

  onBackdropClick(event: Event): void {
    this.closeSchedule(event, this.webinarSchedule);
  }

  toggleDetails(event: Event, index: number): void {
    const target = event.target as HTMLElement;
    const detailsElement = target.closest('details');

    if (detailsElement) {
      this.openSessions[index] = detailsElement.open;
    }
  }

  /**
   * Groups events by eventId for a given session
   */
  getGroupedEvents(session: WebinarScheduleSessionList): { [eventId: string]: WebinarScheduleEventList[] } {
    const grouped: { [eventId: string]: WebinarScheduleEventList[] } = {};

    session.eventsList
      .filter((event: WebinarScheduleEventList) => event.eventId) // Filter out events without a valid eventId
      .forEach((event: WebinarScheduleEventList) => {
        const eventId = event.eventId;
        if (!grouped[eventId]) {
          grouped[eventId] = [];
        }
        grouped[eventId].push(event);
      });

    return grouped;
  }

  /**
   * Gets the keys (eventIds) from grouped events
   */
  getGroupedEventKeys(groupedEvents: { [eventId: string]: WebinarScheduleEventList[] }): string[] {
    // Check if data is null or undefined before calling Object.keys()
    if (groupedEvents === null || groupedEvents === undefined) {
      return []; // or handle the case appropriately
    }
    return Object.keys(groupedEvents);
  }

  /**
   * Formats multiple events in a group for display with day labels
   */
  formatGroupedEventWithDays(
    events: WebinarScheduleEventList[],
  ): Array<{ day: string; time: string; dayLabel: string }> {
    return events.map((event, index) => ({
      day: event.day,
      time: event.time,
      dayLabel: `Day ${index + 1}`,
    }));
  }

  /**
   * Check if there are multiple event groups (useful for showing separators)
   */
  hasMultipleEventGroups(session: WebinarScheduleSessionList): boolean {
    return this.getGroupedEventKeys(this.getGroupedEvents(session)).length > 1;
  }
}
